/* eslint-disable jsx-a11y/alt-text */
import React, { useMemo, useState } from "react";
// @mui
import { styled } from "@mui/material/styles";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LockIcon from "@mui/icons-material/Lock";
import { useNavigate } from "react-router-dom";
// hooks
import useResponsive from "../hooks/useResponsive";

// components
import LandingPageNav from "../components/landingpage/LandingPageNav";

// OTP API
import { otpStart, otpVerify } from "../service/otpApi";

// ----------------------------------------------------------------------

const StyledRoot = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}));

const StyledSection = styled("div")(({ theme }) => ({
  width: "100%",
  maxWidth: 480,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  boxShadow: theme.customShadows.card,
  backgroundColor: theme.palette.background.default,
}));

const StyledContent = styled("div")(({ theme }) => ({
  maxWidth: 480,
  margin: "auto",
  minHeight: "100vh",
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(12, 0),
}));

function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}

function formatPhoneDisplay(phone) {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

// ----------------------------------------------------------------------

export default function BorrowerLoginPage() {
  const mdUp = useResponsive("up", "md");
  const navigate = useNavigate();

  const [step, setStep] = useState("input"); // "input" | "code"
  const [method, setMethod] = useState("email"); // "email" | "sms"
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");

  const [loading, setLoading] = useState(false);
  const [infoMsg, setInfoMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const canSend = useMemo(() => {
    if (method === "email") {
      return normalizeEmail(emailAddress).includes("@");
    }
    // SMS: must have 10 or 11 digits
    const digits = phoneNumber.replace(/\D/g, "");
    return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
  }, [method, emailAddress, phoneNumber]);

  const canVerify = useMemo(
    () => code.replace(/\D/g, "").length === 6,
    [code]
  );

  const handleMethodChange = (e, newMethod) => {
    if (newMethod !== null) {
      setMethod(newMethod);
      setErrorMsg("");
      setInfoMsg("");
    }
  };

  const handleSendCode = async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      setInfoMsg("");

      if (method === "email") {
        const email = normalizeEmail(emailAddress);
        if (!email) {
          setErrorMsg("Please enter your email address.");
          return;
        }
        const res = await otpStart({ method: "email", emailAddress: email });
        if (res?.status === 200) {
          setStep("code");
          setInfoMsg("We sent a 6-digit verification code to your email.");
        } else {
          setErrorMsg(res?.data?.message || "Unable to send code. Please try again.");
        }
      } else {
        const digits = phoneNumber.replace(/\D/g, "");
        if (digits.length < 10) {
          setErrorMsg("Please enter a valid phone number.");
          return;
        }
        const res = await otpStart({ method: "sms", phoneNumber: digits });
        if (res?.status === 200) {
          setStep("code");
          setInfoMsg("We sent a 6-digit verification code to your phone.");
        } else {
          setErrorMsg(res?.data?.message || "Unable to send code. Please try again.");
        }
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.response?.data?.message || "Unable to send code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      setInfoMsg("");

      const otp = code.replace(/\D/g, "").slice(0, 6);
      if (otp.length !== 6) {
        setErrorMsg("Please enter the 6-digit code.");
        return;
      }

      let res;
      if (method === "email") {
        const email = normalizeEmail(emailAddress);
        if (!email) {
          setErrorMsg("Missing email address.");
          setStep("input");
          return;
        }
        res = await otpVerify({ method: "email", emailAddress: email, code: otp });
      } else {
        const digits = phoneNumber.replace(/\D/g, "");
        if (digits.length < 10) {
          setErrorMsg("Missing phone number.");
          setStep("input");
          return;
        }
        res = await otpVerify({ method: "sms", phoneNumber: digits, code: otp });
      }

      if (res?.status === 200) {
        const { token, borrower } = res.data;
        sessionStorage.setItem("borrowerToken", token);
        sessionStorage.setItem("borrowerUser", JSON.stringify(borrower));
        navigate("/borrower/loans");
      } else {
        setErrorMsg(res?.data?.message || "Invalid code. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.response?.data?.message || "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setErrorMsg("");
    setInfoMsg("");
    setCode("");
    setStep("input");
  };

  const handleResend = async () => {
    await handleSendCode();
  };

  const sentToLabel =
    method === "email"
      ? normalizeEmail(emailAddress)
      : formatPhoneDisplay(phoneNumber);

  return (
    <>
      <LandingPageNav />

      <StyledRoot>
        {mdUp && (
          <StyledSection>
            <Typography variant="h3" sx={{ px: 5, mt: 10, mb: 5 }}>
              Hi, Welcome Back
            </Typography>
            <img src="/assets/JET_stacked_color 480 x 480-01.jpg" alt="login" />
          </StyledSection>
        )}

        <Container maxWidth="sm">
          <StyledContent>
            <Typography variant="h4" gutterBottom mb={2} sx={{ textAlign: "center" }}>
              Borrower Portal
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mb: 4 }}
            >
              Sign in with a one-time verification code.
            </Typography>

            <Box sx={{ width: "100%" }}>
              {errorMsg && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMsg}
                </Alert>
              )}
              {infoMsg && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {infoMsg}
                </Alert>
              )}

              {step === "input" ? (
                <Stack spacing={2}>
                  <ToggleButtonGroup
                    value={method}
                    exclusive
                    onChange={handleMethodChange}
                    fullWidth
                    size="small"
                    sx={{ mb: 1 }}
                  >
                    <ToggleButton value="email">
                      <EmailIcon sx={{ mr: 1 }} /> Email
                    </ToggleButton>
                    <ToggleButton value="sms">
                      <PhoneIcon sx={{ mr: 1 }} /> Text Message
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {method === "email" ? (
                    <TextField
                      fullWidth
                      label="Email Address"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      autoComplete="email"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      type="tel"
                      autoComplete="tel"
                      placeholder="(555) 123-4567"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    disabled={!canSend || loading}
                    onClick={handleSendCode}
                  >
                    {loading ? <CircularProgress size={22} /> : "Send Code"}
                  </Button>

                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {method === "email"
                      ? "We'll send a 6-digit code to your email. It expires in 10 minutes."
                      : "We'll text a 6-digit code to your phone. It expires in 10 minutes."}
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Alert severity="info" sx={{ mb: 0 }}>
                    Code sent to <b>{sentToLabel}</b>
                  </Alert>

                  <TextField
                    fullWidth
                    label="6-Digit Code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    disabled={!canVerify || loading}
                    onClick={handleVerifyCode}
                  >
                    {loading ? <CircularProgress size={22} /> : "Verify & Sign In"}
                  </Button>

                  <Stack direction="row" spacing={2} justifyContent="space-between">
                    <Button variant="text" onClick={handleBack} disabled={loading}>
                      {method === "email" ? "Change Email" : "Change Number"}
                    </Button>
                    <Button variant="text" onClick={handleResend} disabled={loading}>
                      Resend Code
                    </Button>
                  </Stack>

                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {method === "email"
                      ? "If you don't see the email, check spam/junk."
                      : "If you don't receive the text, try resending or switch to email."}
                  </Typography>
                </Stack>
              )}
            </Box>
          </StyledContent>
        </Container>
      </StyledRoot>
    </>
  );
}
